import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Modal, Field, TextInput, Select, Textarea, FormActions, dbErrorMessage } from './form';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PROPERTY_TYPES = ['villa', 'apartment', 'house', 'restaurant', 'bar', 'hotel', 'building', 'other'] as const;
const COMMISSION_TYPES = ['percentage', 'flat'] as const;
const COMMISSION_BASES = ['first_contract', 'recurring'] as const;

type Base<T> = { open: boolean; onClose: () => void; onSaved: () => void; initial?: T | null };

// ── Lead ─────────────────────────────────────────────────────────────────────
export type LeadRow = {
  id: string; name: string; contact_name: string | null; email: string | null; phone: string | null;
  property_type: string | null; estimated_value: number | null; currency: string | null;
  stage_id: string; source_id: string | null; referrer_id: string | null; next_follow_up_at: string | null; notes?: string | null;
};
type Opt = { id: string; name: string; name_i18n?: Record<string, string> | null };
const optLabel = (o: Opt, lng: string) => o.name_i18n?.[lng] || o.name;

export function LeadForm({ open, onClose, onSaved, initial }: Base<LeadRow>) {
  const { t, i18n } = useTranslation();
  const { id: orgId, currency } = useOrg();
  const [stages, setStages] = useState<Opt[]>([]);
  const [sources, setSources] = useState<Opt[]>([]);
  const [referrers, setReferrers] = useState<Opt[]>([]);
  const [f, setF] = useState({ name: '', contact_name: '', email: '', phone: '', property_type: '', estimated_value: '', stage_id: '', source_id: '', referrer_id: '', next_follow_up_at: '', notes: '' });
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErr({});
    (async () => {
      const [st, sr, rf] = await Promise.all([
        supabase.from('pipeline_stages').select('id,name,name_i18n').is('deleted_at', null).order('position'),
        supabase.from('lead_sources').select('id,name,name_i18n').is('deleted_at', null).order('name'),
        supabase.from('referrers').select('id,name').is('deleted_at', null).eq('active', true).order('name'),
      ]);
      setStages((st.data as Opt[]) ?? []);
      setSources((sr.data as Opt[]) ?? []);
      setReferrers((rf.data as Opt[]) ?? []);
      setF({
        name: initial?.name ?? '', contact_name: initial?.contact_name ?? '', email: initial?.email ?? '', phone: initial?.phone ?? '',
        property_type: initial?.property_type ?? '', estimated_value: initial?.estimated_value != null ? String(initial.estimated_value) : '',
        stage_id: initial?.stage_id ?? (st.data as Opt[])?.[0]?.id ?? '', source_id: initial?.source_id ?? '',
        referrer_id: initial?.referrer_id ?? '', next_follow_up_at: initial?.next_follow_up_at ?? '', notes: initial?.notes ?? '',
      });
    })();
  }, [open, initial]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const er: Record<string, string> = {};
    if (!f.name.trim()) er.name = t('form.errRequired');
    if (f.email.trim() && !EMAIL_RE.test(f.email.trim())) er.email = t('form.errEmail');
    if (!f.stage_id) er.stage_id = t('form.errRequired');
    setErr(er);
    if (Object.keys(er).length) return;
    setBusy(true);
    const payload = {
      name: f.name.trim(), contact_name: f.contact_name.trim() || null, email: f.email.trim() || null, phone: f.phone.trim() || null,
      property_type: f.property_type || null, estimated_value: f.estimated_value ? Number(f.estimated_value) : null, currency,
      stage_id: f.stage_id, source_id: f.source_id || null, referrer_id: f.referrer_id || null,
      next_follow_up_at: f.next_follow_up_at || null, notes: f.notes.trim() || null,
    };
    const res = initial?.id
      ? await supabase.from('leads').update(payload).eq('id', initial.id)
      : await supabase.from('leads').insert({ ...payload, organization_id: orgId } as any);
    setBusy(false);
    if (res.error) { setErr({ _: dbErrorMessage(t, res.error) }); return; }
    onSaved(); onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title={initial?.id ? t('crm.editLead') : t('crm.newLead')} wide>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err._ && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err._}</div>}
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('crm.leadName')} required error={err.name}><TextInput value={f.name} error={!!err.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus /></Field>
          <Field label={t('crm.contactName')}><TextInput value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} /></Field>
          <Field label={t('form.email')} error={err.email}><TextInput type="email" value={f.email} error={!!err.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          <Field label={t('form.phone')}><TextInput value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
          <Field label={t('crm.propertyType')}>
            <Select value={f.property_type} onChange={(e) => setF({ ...f, property_type: e.target.value })}>
              <option value="">—</option>
              {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{t('propertyType.' + p)}</option>)}
            </Select>
          </Field>
          <Field label={`${t('crm.estimatedValue')} (${currency})`}><TextInput type="number" min="0" step="0.01" value={f.estimated_value} onChange={(e) => setF({ ...f, estimated_value: e.target.value })} /></Field>
          <Field label={t('crm.stage')} required error={err.stage_id}>
            <Select value={f.stage_id} error={!!err.stage_id} onChange={(e) => setF({ ...f, stage_id: e.target.value })}>
              {stages.map((s) => <option key={s.id} value={s.id}>{optLabel(s, i18n.language)}</option>)}
            </Select>
          </Field>
          <Field label={t('crm.source')}>
            <Select value={f.source_id} onChange={(e) => setF({ ...f, source_id: e.target.value })}>
              <option value="">—</option>
              {sources.map((s) => <option key={s.id} value={s.id}>{optLabel(s, i18n.language)}</option>)}
            </Select>
          </Field>
          <Field label={t('crm.referrer')}>
            <Select value={f.referrer_id} onChange={(e) => setF({ ...f, referrer_id: e.target.value })}>
              <option value="">—</option>
              {referrers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </Select>
          </Field>
          <Field label={t('crm.nextFollowUp')}><TextInput type="date" value={f.next_follow_up_at} onChange={(e) => setF({ ...f, next_follow_up_at: e.target.value })} /></Field>
        </div>
        <Field label={t('form.notes')}><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}

// ── Referrer ─────────────────────────────────────────────────────────────────
export type ReferrerRow = {
  id: string; name: string; email: string | null; phone: string | null;
  commission_type: string; commission_value: number; commission_basis: string; currency: string | null; active: boolean; notes?: string | null;
};
export function ReferrerForm({ open, onClose, onSaved, initial }: Base<ReferrerRow>) {
  const { t } = useTranslation();
  const { id: orgId, currency } = useOrg();
  const [f, setF] = useState({ name: '', email: '', phone: '', commission_type: 'percentage', commission_value: '', commission_basis: 'first_contract', active: true, notes: '' });
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErr({});
    setF({
      name: initial?.name ?? '', email: initial?.email ?? '', phone: initial?.phone ?? '',
      commission_type: initial?.commission_type ?? 'percentage', commission_value: initial?.commission_value != null ? String(initial.commission_value) : '',
      commission_basis: initial?.commission_basis ?? 'first_contract', active: initial?.active ?? true, notes: initial?.notes ?? '',
    });
  }, [open, initial]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const er: Record<string, string> = {};
    if (!f.name.trim()) er.name = t('form.errRequired');
    if (f.email.trim() && !EMAIL_RE.test(f.email.trim())) er.email = t('form.errEmail');
    setErr(er);
    if (Object.keys(er).length) return;
    setBusy(true);
    const payload = {
      name: f.name.trim(), email: f.email.trim() || null, phone: f.phone.trim() || null,
      commission_type: f.commission_type, commission_value: Number(f.commission_value) || 0, commission_basis: f.commission_basis,
      currency: f.commission_type === 'flat' ? currency : null, active: f.active, notes: f.notes.trim() || null,
    };
    const res = initial?.id
      ? await supabase.from('referrers').update(payload).eq('id', initial.id)
      : await supabase.from('referrers').insert({ ...payload, organization_id: orgId } as any);
    setBusy(false);
    if (res.error) { setErr({ _: dbErrorMessage(t, res.error) }); return; }
    onSaved(); onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title={initial?.id ? t('crm.editReferrer') : t('crm.newReferrer')}>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err._ && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err._}</div>}
        <Field label={t('form.name')} required error={err.name}><TextInput value={f.name} error={!!err.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('form.email')} error={err.email}><TextInput type="email" value={f.email} error={!!err.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          <Field label={t('form.phone')}><TextInput value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
          <Field label={t('crm.commissionType')}>
            <Select value={f.commission_type} onChange={(e) => setF({ ...f, commission_type: e.target.value })}>
              {COMMISSION_TYPES.map((c) => <option key={c} value={c}>{t('crm.commType.' + c)}</option>)}
            </Select>
          </Field>
          <Field label={f.commission_type === 'percentage' ? t('crm.commissionPct') : `${t('crm.commissionAmount')} (${currency})`}>
            <TextInput type="number" min="0" step="0.01" value={f.commission_value} onChange={(e) => setF({ ...f, commission_value: e.target.value })} />
          </Field>
          <Field label={t('crm.commissionBasis')}>
            <Select value={f.commission_basis} onChange={(e) => setF({ ...f, commission_basis: e.target.value })}>
              {COMMISSION_BASES.map((c) => <option key={c} value={c}>{t('crm.commBasis.' + c)}</option>)}
            </Select>
          </Field>
          <Field label={t('crm.active')}>
            <Select value={f.active ? '1' : '0'} onChange={(e) => setF({ ...f, active: e.target.value === '1' })}>
              <option value="1">{t('common.yes')}</option><option value="0">{t('common.no')}</option>
            </Select>
          </Field>
        </div>
        <span className="text-2xs text-muted">{t('crm.commissionHint')}</span>
        <Field label={t('form.notes')}><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}
